import React, { useCallback, useEffect, useState } from "react";
import api, { formatError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Edit3, X, Save, MapPin } from "lucide-react";

const ZONES = ["North", "South", "East", "West", "Central"];
const BLANK = { zone: "North", name: "", designation: "", experience: "", contact_number: "", email: "", whatsapp_number: "", office_address: "", photo_url: "", order: 100 };

export default function AdminExecutives() {
  const [items, setItems] = useState([]);
  const [zoneFilter, setZoneFilter] = useState("all");
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/admin/executives"); setItems(data); }
    catch (e) { toast.error(formatError(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const del = async (row) => {
    if (!window.confirm(`Delete ${row.name}?`)) return;
    try { await api.delete(`/admin/executives/${row.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatError(e)); }
  };

  const visible = zoneFilter === "all" ? items : items.filter((i) => i.zone === zoneFilter);

  return (
    <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6" data-testid="admin-executives-page">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-extrabold sm:text-4xl">Executive Teams</h1>
          <p className="mt-1 text-sm font-medium text-sarthi-muted">Add, edit or remove regional executives. Changes reflect immediately on <code>/executive-teams</code>.</p>
        </div>
        <button data-testid="new-exec-btn" onClick={() => setEditing({ ...BLANK })} className="sarthi-btn-primary inline-flex items-center gap-2 px-4 py-2 text-sm text-white">
          <Plus className="h-4 w-4" /> New Executive
        </button>
      </div>

      <div className="mb-4 flex flex-wrap gap-2">
        {["all", ...ZONES].map((z) => (
          <button key={z} data-testid={`zone-filter-${z}`} onClick={() => setZoneFilter(z)} className={`rounded-full border-2 px-3 py-1.5 text-xs font-bold ${zoneFilter === z ? "border-sarthi-primary bg-sarthi-accent text-sarthi-primary" : "border-sarthi-border bg-white text-sarthi-muted"}`}>
            {z === "all" ? "All" : z} {z !== "all" && `(${items.filter((i) => i.zone === z).length})`}
          </button>
        ))}
      </div>

      <div className="sarthi-card overflow-x-auto">
        <table className="w-full text-left">
          <thead className="border-b-2 border-sarthi-border bg-sarthi-accent/50 text-xs font-bold uppercase text-sarthi-muted">
            <tr><th className="px-3 py-3">Zone</th><th className="px-3 py-3">Name</th><th className="px-3 py-3">Experience</th><th className="px-3 py-3">Contact</th><th className="px-3 py-3">Office</th><th className="px-3 py-3">Order</th><th className="px-3 py-3 text-right">Actions</th></tr>
          </thead>
          <tbody>
            {visible.map((row) => (
              <tr key={row.id} data-testid={`exec-row-${row.id}`} className="border-b border-sarthi-border/60">
                <td className="px-3 py-3 font-bold text-sarthi-primary">{row.zone}</td>
                <td className="px-3 py-3">
                  <div className="font-semibold">{row.name}</div>
                  <div className="text-xs text-sarthi-muted">{row.designation || ""}</div>
                </td>
                <td className="px-3 py-3 text-xs">{row.experience || "—"}</td>
                <td className="px-3 py-3 text-xs">
                  <div>{row.contact_number}</div>
                  {row.whatsapp_number && <div className="text-emerald-700">wa: {row.whatsapp_number}</div>}
                </td>
                <td className="px-3 py-3 text-xs max-w-[220px]"><MapPin className="mr-1 inline h-3 w-3" />{row.office_address}</td>
                <td className="px-3 py-3 text-xs">{row.order}</td>
                <td className="px-3 py-3 text-right">
                  <button data-testid={`edit-${row.id}`} onClick={() => setEditing({ ...row })} className="mr-2 text-sarthi-primary"><Edit3 className="h-4 w-4" /></button>
                  <button data-testid={`del-${row.id}`} onClick={() => del(row)} className="text-red-700"><Trash2 className="h-4 w-4" /></button>
                </td>
              </tr>
            ))}
            {visible.length === 0 && <tr><td colSpan={7} className="p-8 text-center text-sarthi-muted">No executives{zoneFilter !== "all" && ` in ${zoneFilter}`}</td></tr>}
          </tbody>
        </table>
      </div>

      {editing && <EditModal row={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function EditModal({ row, onClose, onSaved }) {
  const isNew = !row.id;
  const [f, setF] = useState({ ...row });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async () => {
    const body = { ...f, order: Number(f.order) || 100 };
    try {
      if (isNew) await api.post("/admin/executives", body);
      else await api.patch(`/admin/executives/${row.id}`, body);
      toast.success("Saved"); onSaved();
    } catch (e) { toast.error(formatError(e)); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose} data-testid="exec-modal">
      <div className="sarthi-card w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
        <div className="mb-3 flex items-center justify-between"><h2 className="font-display text-xl font-extrabold">{isNew ? "New Executive" : `Edit — ${row.name}`}</h2><button onClick={onClose}><X className="h-5 w-5" /></button></div>
        <div className="grid grid-cols-2 gap-3">
          <select data-testid="ex-zone" value={f.zone} onChange={set("zone")} className="rounded-xl border-2 border-sarthi-border px-3 py-2 font-bold">{ZONES.map((z) => <option key={z} value={z}>{z}</option>)}</select>
          <input data-testid="ex-order" type="number" placeholder="Order (lower = first)" value={f.order} onChange={set("order")} className="rounded-xl border-2 border-sarthi-border px-3 py-2" />
          <input data-testid="ex-name" placeholder="Full name" value={f.name} onChange={set("name")} className="col-span-2 rounded-xl border-2 border-sarthi-border px-3 py-2" />
          <input data-testid="ex-designation" placeholder="Designation (e.g. Regional Head)" value={f.designation} onChange={set("designation")} className="rounded-xl border-2 border-sarthi-border px-3 py-2" />
          <input data-testid="ex-experience" placeholder="Experience (e.g. 18 yrs in thermal)" value={f.experience} onChange={set("experience")} className="rounded-xl border-2 border-sarthi-border px-3 py-2" />
          <input data-testid="ex-contact" placeholder="Contact number (10-digit)" value={f.contact_number} onChange={set("contact_number")} className="rounded-xl border-2 border-sarthi-border px-3 py-2" />
          <input data-testid="ex-whatsapp" placeholder="WhatsApp number (optional)" value={f.whatsapp_number} onChange={set("whatsapp_number")} className="rounded-xl border-2 border-sarthi-border px-3 py-2" />
          <input data-testid="ex-email" placeholder="Email (optional)" value={f.email} onChange={set("email")} className="col-span-2 rounded-xl border-2 border-sarthi-border px-3 py-2" />
          <textarea data-testid="ex-office" placeholder="Regional office address" value={f.office_address} onChange={set("office_address")} rows={3} className="col-span-2 rounded-xl border-2 border-sarthi-border px-3 py-2" />
          <input data-testid="ex-photo" placeholder="Photo URL (optional)" value={f.photo_url} onChange={set("photo_url")} className="col-span-2 rounded-xl border-2 border-sarthi-border px-3 py-2" />
        </div>
        <button data-testid="ex-save" onClick={save} className="sarthi-btn-primary mt-4 inline-flex w-full items-center justify-center gap-2 py-2 text-white"><Save className="h-4 w-4" /> Save</button>
      </div>
    </div>
  );
}
